<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Ground station"
          >
            A ground station is defined by its latitude, longitude, altitude and
            minimum elevation <katex-element expression="\alpha_e" />. The
            spacecraft shares data with the station as soon as it enters the
            visibility cone of the station.
            <br />
            <br />
            <v-img
              contain
              :src="
                require('../../assets/images/tooltip-ground-station-fov.png')
              "
              aspect-ratio="3"
            />
            <br />
            <p>
              Ground stations can also be used to perform a link budget study,
              establishing a connection with a spacecraft that possesses an RF
              antenna, for uplink or downlink (as receiver and/or transmitter,
              respectively).
            </p>
          </exo-help-dialog>
        </template>
        <v-row no-gutters>
          <v-col cols="5" class="form-column">
            <exo-input-name :root="_self" />
            <v-divider></v-divider>
            <exo-input-double :root="_self" name="longitude" />
            <exo-input-double :root="_self" name="latitude" />
            <exo-input-double :root="_self" name="groundStationAltitude" />
            <exo-input-double :root="_self" name="minElevation">
              <exo-help-dialog title="Minimum elevation">
                The minimum elevation <katex-element expression="\alpha_e" />
                defines the field of view of the ground station.
                <br />
                <br />
                <v-img
                  contain
                  :src="
                    require('../../assets/images/tooltip-ground-station-fov.png')
                  "
                  aspect-ratio="3"
                />
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-select :root="_self" name="rfObjectType" />
            <exo-input-select :root="_self" name="rfDownlinkDefinitionType" />
            <exo-input-select :root="_self" name="rfUplinkDefinitionType" />
            <exo-input-double :root="_self" name="eirp" />
            <exo-input-double :root="_self" name="gainToNoiseTemperature" />
            <exo-input-double :root="_self" name="systemNoiseTemperature">
              <tooltipSystemNoiseTemperature />
            </exo-input-double>
            <exo-input-double :root="_self" name="receiverLineLosses">
              <tooltipLineLosses />
            </exo-input-double>
            <exo-input-double :root="_self" name="receiverGain">
              <tooltipAntennaGain />
            </exo-input-double>
            <exo-input-double :root="_self" name="receiverBeamwidth">
              <tooltipBeamWidth />
            </exo-input-double>
            <exo-input-double :root="_self" name="receiverPointingPrecision">
              <tooltipPointingPrecision />
            </exo-input-double>
            <exo-input-double :root="_self" name="transmittedPower">
              <tooltipTransmittedPower />
            </exo-input-double>
            <exo-input-double :root="_self" name="transmitterLineLosses">
              <tooltipLineLosses />
            </exo-input-double>
            <exo-input-double :root="_self" name="transmitterGain">
              <tooltipAntennaGain />
            </exo-input-double>
            <exo-input-double :root="_self" name="transmitterBeamwidth">
              <tooltipBeamWidth />
            </exo-input-double>
            <exo-input-double :root="_self" name="transmitterPointingPrecision">
              <tooltipPointingPrecision />
            </exo-input-double>
          </v-col>
          <v-col cols="7">
            <exo-form-alert
              type="warning"
              v-if="errorComputGsAreaMessage !== ''"
            >
              {{ errorComputGsAreaMessage }}
            </exo-form-alert>
            <cesium-viewer
              :gsData="groundStationData"
              v-on:locationPinned="updateInputs"
            />
            <v-row no-gutters>
              <v-text-field
                v-model="inputs.spacecraftAltitude"
                label="Spacecraft altitude (for visualization only)"
                :rules="[spacecraftAltitudeFeedback]"
              ></v-text-field>
              <v-col cols="1"> </v-col>
            </v-row>
            <exo-form-alert type="warning" v-if="spacecraftAltitudeWarning">
              Warning: the spacecraft altitude used in the ExoOPS - Mission
              Design™ missions must be between 250 km and 50000
              km.</exo-form-alert
            >
          </v-col>
        </v-row>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import constellationComponentsLoader from '../../components/Forms/analyticalConstellationsComponents.js'
import { mapGetters, mapActions } from 'vuex'
import dataTools from '../../tools/data'
import fieldsDescription from '../../tools/fieldsDescription'
import GroundStationViewer from './GroundStationViewer.vue'
import './formsStyle.css'
import tooltipSystemNoiseTemperature from '../../components/Tooltips/Simulations/LinkBudget/tooltipSystemNoiseTemperature.vue'
import tooltipLineLosses from '../../components/Tooltips/Simulations/LinkBudget/tooltipLineLosses.vue'
import tooltipAntennaGain from '../../components/Tooltips/Simulations/LinkBudget/tooltipAntennaGain.vue'
import tooltipBeamWidth from '../../components/Tooltips/Simulations/LinkBudget/tooltipBeamWidth.vue'
import tooltipPointingPrecision from '../../components/Tooltips/Simulations/LinkBudget/tooltipPointingPrecision.vue'
import tooltipTransmittedPower from '../../components/Tooltips/Simulations/LinkBudget/tooltipTransmittedPower.vue'

constellationComponentsLoader()

export default {
  name: 'AnalyticalConstellationGroundStation',

  mixins: [formMixin, formAnalyticalMixin],
  components: {
    'cesium-viewer': GroundStationViewer,
    tooltipSystemNoiseTemperature,
    tooltipLineLosses,
    tooltipAntennaGain,
    tooltipBeamWidth,
    tooltipPointingPrecision,
    tooltipTransmittedPower
  },

  data() {
    return {
      groundStationView: null,
      map: null,
      displayDomName: 'groundStationMap',
      fieldOfViewRadius: null,
      fieldOfViewPlolygon: null,
      errorComputGsAreaMessage: null,
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'longitude',
          text: 'Longitude' + this.getDefaultUnitLabel('longitude'),
          sortable: true
        },
        {
          value: 'latitude',
          text: 'Latitude' + this.getDefaultUnitLabel('latitude'),
          sortable: true
        },
        {
          value: 'minElevation',
          text: 'Min elevation' + this.getDefaultUnitLabel('minElevation'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,

      enumPropertiesList: this.getEnumProperties(),
      stationFieldOfViewCoordinates: [],
      stationFieldOfViewRightCoordinates: [],
      stationFieldOfViewLeftCoordinates: [],

      inputs: {
        rfUplinkDefinitionType: this.getDefaultValue('rfUplinkDefinitionType'),
        rfDownlinkDefinitionType: this.getDefaultValue(
          'rfDownlinkDefinitionType'
        ),
        eirp: this.getDefaultValue('eirp'),
        gainToNoiseTemperature: this.getDefaultValue('gainToNoiseTemperature'),
        stationName: this.getDefaultValue('stationName'),
        latitude: this.getDefaultValue('latitude'),
        longitude: this.getDefaultValue('longitude'),
        spacecraftAltitude: 500,
        minElevation: this.getDefaultValue('minElevation'),
        rfObjectType: this.getDefaultValue('rfObjectType'),
        VisibilityChoice: this.getDefaultValue('VisibilityChoice'),
        groundStationAltitude: this.getDefaultValue('groundStationAltitude'),
        systemNoiseTemperature: this.getDefaultValue('systemNoiseTemperature'),
        receiverLineLosses: this.getDefaultValue('receiverLineLosses'),
        receiverGain: this.getDefaultValue('receiverGain'),
        receiverBeamwidth: this.getDefaultValue('receiverBeamwidth'),
        receiverPointingPrecision: this.getDefaultValue(
          'receiverPointingPrecision'
        ),
        transmittedPower: this.getDefaultValue('transmittedPower'),
        transmitterLineLosses: this.getDefaultValue('transmitterLineLosses'),
        transmitterGain: this.getDefaultValue('transmitterGain'),
        transmitterBeamwidth: this.getDefaultValue('transmitterBeamwidth'),
        transmitterPointingPrecision: this.getDefaultValue(
          'transmitterPointingPrecision'
        ),
        scope: this.getDefaultValue('scope')
      },
      groundStationData: {
        longitude: parseFloat(this.getDefaultValue('longitude')),
        latitude: parseFloat(this.getDefaultValue('latitude')),
        fieldOfViewRadius: 0
      }
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),

    getGroundStationFoVRadius() {
      let station = this.createObjectFromForm()
      station.operatingAltitude = dataTools.convertGui2si(
        'distance',
        this.inputs.spacecraftAltitude
      )
      const fovRadius = dataTools.buildStationViewRadius(
        station.operatingAltitude,
        station.minElevation,
        0
      )
      return fovRadius ? fovRadius : 0
    },

    getCurrentFormTitle() {
      return 'Ground Station'
    },

    getCurrentFormType() {
      return 'GroundStationDefinition'
    },

    updateInputs(cartoPos) {
      this.inputs.longitude = cartoPos.longitude
      this.inputs.latitude = cartoPos.latitude
    },

    getGroundStationType(instance) {
      let types = {
        rfObjectType: instance.rfObjectType,
        rfDownlinkDefinitionType: instance.rfDownlinkDefinitionType,
        rfUplinkDefinitionType: instance.rfUplinkDefinitionType,
        VisibilityChoice: instance.VisibilityChoice
      }
      return types
    },

    getRequiredPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getGroundStationType(instance),
        'required'
      )
    },

    getOptionalPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getGroundStationType(instance),
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    }
  },

  computed: {
    ...mapGetters(['currentMission']),

    latitudeLongitude() {
      return { lat: this.inputs.latitude, lng: this.inputs.longitude }
    },

    spacecraftAltitudeFeedback() {
      let feedback = true
      if (this.inputs.spacecraftAltitude !== '') {
        let altitude = Number(this.inputs.spacecraftAltitude)
        if (altitude !== null) {
          if (isNaN(altitude) || altitude <= 0) {
            feedback = 'Please input a valid positive number.'
          }
        }
      }

      return feedback
    },

    spacecraftAltitudeWarning() {
      if (this.inputs.spacecraftAltitude !== '') {
        let altitude = Number(this.inputs.spacecraftAltitude)
        if (altitude !== null) {
          if (!isNaN(altitude) && (altitude < 250 || altitude > 50000)) {
            return true
          }
        }
      }
      return false
    }
  },

  watch: {
    inputs: {
      handler() {
        setTimeout(() => {
          this.groundStationData = {
            longitude: parseFloat(this.inputs.longitude),
            latitude: parseFloat(this.inputs.latitude),
            fieldOfViewRadius: this.getGroundStationFoVRadius()
          }
        }, 500)
      },
      deep: true
    }
  }
}
</script>
